import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Download, FileText, Lock, ShoppingBag } from "lucide-react";
import { PortalShell } from "./PortalShell";
import { portalApi, portalErr, inr, loadRazorpay, openPdf } from "./portalApi";
import "../test/testTheme.css";

export function useBuy(onDone) {
  const [buying, setBuying] = useState("");
  const [error, setError] = useState("");

  const buy = async (itemType, itemId, label) => {
    setBuying(itemId);
    setError("");
    try {
      const { data: order } = await portalApi.post("/store/order", { item_type: itemType, item_id: itemId });
      const loaded = await loadRazorpay();
      if (!loaded) throw new Error("checkout load failed");
      const rzp = new window.Razorpay({
        key: order.key_id, amount: order.amount, currency: "INR",
        name: "Pranjal Sir Physics", description: label,
        order_id: order.rzp_order_id,
        prefill: { name: order.student.name, email: order.student.email, contact: order.student.mobile },
        theme: { color: "#f77f00" },
        handler: async (resp) => {
          try {
            await portalApi.post("/store/verify", { order_id: order.order_id, razorpay_order_id: resp.razorpay_order_id, razorpay_payment_id: resp.razorpay_payment_id, razorpay_signature: resp.razorpay_signature });
            onDone(order);
          } catch (err) { setError(portalErr(err)); }
        },
        modal: { ondismiss: () => setBuying("") },
      });
      rzp.on("payment.failed", () => { setError("Payment failed or was cancelled — you have not been charged for failed attempts. Try again."); setBuying(""); });
      rzp.open();
    } catch (err) {
      setError(err.message === "checkout load failed" ? "Could not load the payment page. Check your connection." : portalErr(err));
      setBuying("");
    }
  };
  return { buy, buying, error, setError, setBuying };
}

function StoreInner() {
  const navigate = useNavigate();
  const [series, setSeries] = useState(null);
  const [products, setProducts] = useState([]);
  const [owned, setOwned] = useState(new Set());
  const [paymentsLive, setPaymentsLive] = useState(false);
  const [notice, setNotice] = useState("");
  const { buy, buying, error, setError, setBuying } = useBuy((order) => {
    setNotice(`Payment verified — "${order.item_name}" is now in My Purchases.`);
    setOwned((c) => new Set([...c, `${order.item_type}:${order.item_id}`]));
    setBuying("");
  });

  const load = () => {
    portalApi.get("/store/series").then(({ data }) => { setSeries(data.series); setPaymentsLive(data.payments_live); }).catch(() => setError("Could not load the store. Please refresh."));
    portalApi.get("/store/products").then(({ data }) => setProducts(data.products)).catch(() => {});
    portalApi.get("/store/my-purchases").then(({ data }) => setOwned(new Set(data.purchases.map((p) => `${p.item_type}:${p.item_id}`)))).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, []);

  return (
    <div className="tp-wrap" style={{ maxWidth: 920, padding: "44px 22px 70px" }}>
      <div className="tp-eyebrow">TEST SERIES STORE</div>
      <h1 className="tp-h2" style={{ fontSize: 32 }} data-testid="store-title">Teacher-made Physics test papers</h1>
      <p className="tp-muted">Preview the sample free. The full paper and solutions unlock instantly after secure payment.</p>
      {notice && <p className="tp-pill ok" data-testid="store-notice"><Check size={14} /> {notice} <button className="tp-back" style={{ background: "none", border: "none", cursor: "pointer" }} onClick={() => navigate("/portal/purchases")}>Go to My Purchases →</button></p>}
      {error && <p className="tp-error" role="alert" data-testid="store-error">{error}</p>}
      {!paymentsLive && <p className="tp-pill warn" data-testid="store-payments-pending"><Lock size={13} /> Online payments are being activated — you can preview samples now; purchases open shortly.</p>}
      {!series ? <p className="tp-muted">Loading…</p> : series.length === 0 ? (
        <div className="tp-panel" style={{ textAlign: "center" }} data-testid="store-empty"><p className="tp-muted">New test papers are being prepared — check back soon.</p></div>
      ) : (
        <div className="tp-cards" data-testid="store-series">
          {series.map((s, i) => {
            const ownedKey = `series:${s.series_id}`;
            return (
              <div className="tp-card" key={s.series_id} data-testid={`store-series-${i}`}>
                <span className="tp-pill info">{s.subject}{s.class_exam ? ` · ${s.class_exam}` : ""}</span>
                <h3 style={{ fontSize: 17 }}>{s.name}</h3>
                {s.chapter && <p className="tp-note" style={{ margin: 0 }}>{s.chapter}{s.test_number ? ` · Test ${s.test_number}` : ""}</p>}
                {s.description && <p>{s.description}</p>}
                <p className="tp-note">{[s.num_questions && `${s.num_questions} questions`, s.max_marks && `${s.max_marks} marks`, s.duration_min && `${s.duration_min} min`, s.has_solutions && "Solutions included"].filter(Boolean).join(" · ") || "Details in the paper"}</p>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8, flexWrap: "wrap" }}>
                  <b style={{ fontSize: 20, color: "#f77f00" }} data-testid={`store-price-${i}`}>{s.price_paise ? inr(s.price_paise) : "FREE"}</b>
                  {s.has_sample && <button className="tp-btn ghost small" onClick={() => openPdf(`/store/series/${s.series_id}/sample`)} data-testid={`store-sample-${i}`}><FileText size={14} /> Free preview</button>}
                  {owned.has(ownedKey)
                    ? <button className="tp-btn secondary small" onClick={() => navigate("/portal/purchases")} data-testid={`store-owned-${i}`}><Check size={14} /> Owned — open</button>
                    : <button className="tp-btn small" disabled={buying === s.series_id || !paymentsLive} onClick={() => buy("series", s.series_id, s.name)} data-testid={`store-buy-${i}`}>{buying === s.series_id ? "Opening…" : <><ShoppingBag size={14} /> Buy now</>}</button>}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {products.length > 0 && <>
        <div className="tp-eyebrow" style={{ marginTop: 34 }}>MORE FROM PRANJAL SIR</div>
        <div className="tp-cards" data-testid="store-products">
          {products.map((p, i) => {
            const ownedKey = `product:${p.product_key}`;
            return (
              <div className="tp-card" key={p.product_key} data-testid={`store-product-${i}`}>
                <h3 style={{ fontSize: 16 }}>{p.name}</h3>
                <p>{p.description}</p>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
                  <b style={{ fontSize: 18, color: "#f77f00" }}>{inr(p.price_paise)}{p.period === "monthly" ? "/month" : ""}</b>
                  {owned.has(ownedKey)
                    ? <span className="tp-pill ok"><Check size={13} /> Active</span>
                    : <button className="tp-btn small" disabled={buying === p.product_key || !paymentsLive} onClick={() => buy("product", p.product_key, p.name)} data-testid={`store-product-buy-${i}`}>{buying === p.product_key ? "Opening…" : "Buy now"}</button>}
                </div>
              </div>
            );
          })}
        </div>
      </>}
      <p className="tp-note" style={{ textAlign: "center", marginTop: 22 }}>Payments are verified on the server before anything unlocks. Failed or cancelled payments never unlock content.</p>
    </div>
  );
}

export default function PortalStore() {
  return <PortalShell><StoreInner /></PortalShell>;
}
